import { useNode } from "@craftjs/core";

import { useUtilities } from "@/modules/cms/views/post/components/builder/context";

import AnimationProperty from "../properties/Animation";
import AttributesProperty from "../properties/Attributes";
import BackgroundProperty from "../properties/Background";
import BorderProperty from "../properties/Border";
import CheckboxProperty, { CheckboxGroupProperty } from "../properties/Checkbox";
import CodeEditorProperty from "../properties/CodeEditor";
import ColorPickerProperty from '../properties/Color';
import DimensionProperty from '../properties/Dimension';
import FiltersProperty from "../properties/Filters";
import FlexStretchProperty from "../properties/FlexStretch";
import FontFamilyProperty from "../properties/FontFamily";
import FontSizeProperty from "../properties/FontSize";
import FrameProperty from "../properties/Frame";
import GapProperty from "../properties/Gap";
import GridCountProperty from "../properties/GridCount";
import GridSizeProperty from "../properties/GridSize";
import GridTemplateProperty from "../properties/GridTemplate";
import HTMLProperty from "../properties/HTML";
import ImageProperty from '../properties/Image';
import ImageAlignProperty from "../properties/ImageAlign";
import InputProperty from '../properties/Input';
import InputGroupProperty from '../properties/InputGroup';
import MaskProperty from "../properties/Mask";
import NativeStyleProperty from "../properties/NativeStyle";
import OverflowProperty from "../properties/Overflow";
import OverscrollProperty from "../properties/Overscroll";
import PositionProperty from "../properties/Position";
import PositionImageProperty from "../properties/PositionImage";
import PredefinedProperty from "../properties/Predefined";
import { ArbitraryUnit, PredefinedArbitraryUnit } from "../properties/PredefinedArbitraryUnit";
import PredefinedGroupProperty from "../properties/PredefinedGroup";
import ProgressRangeProperty from "../properties/ProgressRange";
import RadioGroupProperty from "../properties/RadioGroup";
import ScrollProperty from "../properties/Scroll";
import SelectProperty from "../properties/Select";
import SelectAsyncProperty from "../properties/SelectAsync";
import SelectClassesProperty from "../properties/SelectClasses";
import SelectCreatableProperty from "../properties/SelectCreatable";
import ShadowProperty, { shadowInputPerview } from "../properties/Shadow";
import SharedProperties from "../properties/SharedProperties";
import SliderProperty from "../properties/Slider";
import SpacingProperty from "../properties/Spacing";
import StylePatternProperty from "../properties/StylePattern";
import SwitchProperty from "../properties/Switch";
import TextAlignContainerProperty from "../properties/TextAlign";
import TextColorProperty from "../properties/TextColor";
import TextContentProperty from "../properties/TextContent";
import TextDecorationProperty from "../properties/TextDecoration";
import ToggleGroupProperty from "../properties/ToggleGroup";
import ToggleGroupRowProperty from "../properties/ToggleGroupRow";
import TransformProperty from "../properties/Transform";
import TransitionProperty from "../properties/Transition";
import VariantSelectProperty from "../properties/VariantSelect";
import VisibilityProperty from "../properties/Visibility";
import ZindexProperty from "../properties/Zindex";

import { useCraftProp } from "./hooks";
import { ASPECT_RATIO, FONT_SIZE_SIZE, FONT_WEIGHTS, LETTER_SPACING_SIZE, SHADOW_SIZE, TEXT_SHADOW_SIZE, UNIT_OPTIONS } from "./predefined_sizes";

export const PROPERTIES = {
  code_editor: CodeEditorProperty,
  input: InputProperty,
  slider: SliderProperty,
  radio_group: RadioGroupProperty,
  checkbox: CheckboxProperty,
  checkbox_group: CheckboxGroupProperty,
  color: ColorPickerProperty,
  spacing: SpacingProperty,
  select: SelectProperty,
  variant_select: VariantSelectProperty,
  select_creatable: SelectCreatableProperty,
  select_async: SelectAsyncProperty,
  select_classes: SelectClassesProperty,
  visibility: VisibilityProperty,
  dimension: DimensionProperty,
  toggleGroupRow: ToggleGroupRowProperty,
  fontSize: FontSizeProperty,
  fontFamily: FontFamilyProperty,
  textAlignContainer: TextAlignContainerProperty,
  shared: SharedProperties,
  inputGroup: InputGroupProperty,
  image: ImageProperty,
  toggleGroup: ToggleGroupProperty,
  gridCount: GridCountProperty,
  gridSize: GridSizeProperty,
  overflow: OverflowProperty,
  border: BorderProperty,
  frame: FrameProperty,
  background: BackgroundProperty,
  predefined: PredefinedProperty,
  imageAlign: ImageAlignProperty,
  predefinedGroup: PredefinedGroupProperty,
  position: PositionProperty,
  gap: GapProperty,
  transition: TransitionProperty,
  gridTemplate: GridTemplateProperty,
  flexStretch: FlexStretchProperty,
  animation: AnimationProperty,
  progressRange: ProgressRangeProperty,
  html: HTMLProperty,
  transform: TransformProperty,
  arbitraryUnit: ArbitraryUnit,
  predefinedArbitraryUnit: PredefinedArbitraryUnit,
  nativeStyle: NativeStyleProperty,
  switch: SwitchProperty,
  filters: FiltersProperty,
  zindex: ZindexProperty,
  textContent: TextContentProperty,
  attributes: AttributesProperty,
  textColor: TextColorProperty,
  positionImage: PositionImageProperty,
  textDecoration: TextDecorationProperty,
  overscroll: OverscrollProperty,
  scroll: ScrollProperty,
  mask: MaskProperty,
  stylePattern: StylePatternProperty,
}

export class BaseBuilder {
  constructor(label) {
    this.label(label)
    this._own = false
  }

  type(type) {
    this._type = type
    return this
  }

  component(component) {
    this._component = component
    return this
  }

  label(label) {
    this._label = label
    return this
  }

  name(name) {
    this._name = name
    return this
  }

  category(category) {
    this._category = category
    return this
  }

  options(options) {
    this._options = options
    return this
  }

  props(defaults) {
    this._defaults = defaults
    return this
  }

  useShouldRender(useShouldRender) {
    this._useShouldRender = useShouldRender
    return this
  }

  hint(string) {
    this._hint = string
    return this
  }

  fixedLabel(boolean) {
    this._fixedLabel = boolean
    return this
  }

  keys(array) {
    this._keys = array
    return this
  }

  ignoreDevice(boolean = true) {
    this._ignoreDevice = boolean
    return this
  }

  hide(boolean = true) {
    this.useShouldRender(() => !boolean)
    this._hide = boolean
    return this
  }

  blockClear(boolean = true) {
    this._blockClear = boolean
    return this
  }

  own(boolean = true) {
    this._own = boolean
    return this
  }

  build() {
    const { _label, _name, _type, _component, _options, _defaults, _category, _useShouldRender, _fixedLabel, _ignoreDevice, _hide, _own, _hint, _blockClear, _keys } = this
    return {
      label: _label,
      name: _name,
      type: _type,
      component: _component,
      options: _options,
      category: _category,
      useShouldRender: _useShouldRender,
      fixedLabel: _fixedLabel,
      ignoreDevice: _ignoreDevice,
      hide: _hide,
      own: _own,
      hint: _hint,
      blockClear: _blockClear,
      keys: _keys,
      ..._defaults,
    }
  }
}

class OptionsBuilder extends Array {
  add(label, value = label, props) {
    this.push({ value, label, ...props })
    return this
  }
}

export const prop = new class Params {
  types = PROPERTIES

  group(label, name) {
    return {
      unique() {
        return new BaseBuilder(label).name(name).type('options').component(prop.types.select)
      },
      multi() {
        return new BaseBuilder(label).type('options').component(prop.types.checkbox_group)
      },
    }
  }

  get select() {
    const select = (label, name) => new BaseBuilder(label).name(name).type('options').component(prop.types.select)
    select.creatable = (label, name) => select(label, name).component(prop.types.select_creatable)
    select.async = (label, name) => select(label, name).component(prop.types.select_async)
    select.classes = (label, name) => select(label, name).component(prop.types.select_classes)
    return select
  }

  toggle(label, name) {
    return new BaseBuilder(label).name(name).type('options').component(prop.types.toggleGroup)
  }

  boolean(label, name) {
    return new BaseBuilder(label).name(name).type('boolean').component(prop.types.checkbox)
  }

  switch(label, name) {
    return new BaseBuilder(label).name(name).type('boolean').component(prop.types.switch)
  }

  color(label, name) {
    return new BaseBuilder(label).name(name).type('text').component(prop.types.color)
  }

  text(label, name) {
    return new BaseBuilder(label).name(name).type('text').component(prop.types.input)
  }

  code(label, name) {
    return new BaseBuilder(label).name(name).type('html').component(prop.types.html)
  }

  slider(label, name) {
    return new BaseBuilder(label).name(name).type('number').component(prop.types.slider)
  }

  spacing(label, name) {
    return new BaseBuilder(label).name(name).type('object').component(prop.types.spacing)
  }

  new(label, name, type) {
    return new BaseBuilder(label).name(name).type(type)
  }

  share(name, type) {
    return this.new('Style Pattern', name, type).component(prop.types.shared).category('default')
  }

  stylePattern(name = '') {
    return this.new('Style Pattern', 'stylePattern', 'options')
      .component(prop.types.stylePattern)
      .props({ name })
      .blockClear(false)
      .keys(['stylePattern'])
      .category('default')
      .ignoreDevice(true)
  }

  options() {
    return new OptionsBuilder()
  }

  value(value, type = typeof value) {
    return { value, type }
  }

  get border() {
    return {
      border: () => this.new('Border', 'border', 'border').category('borders')
        .props({
          prefix: 'border',
          itens: ['width', 'style', 'color', 'radius'],
          widthOptions: ['all', 'axis', 'individual'],
        }),
      ring: () => this.new('Ring', 'ring', 'border').category('borders')
        .props({
          prefix: 'ring',
          itens: ['width', 'color', 'offset'],
          widthOptions: ['all'],
        }),
      outline: () => this.new('Outline', 'outline', 'border').category('borders')
        .props({
          prefix: 'outline',
          itens: ['width', 'style', 'color', 'offset'],
          widthOptions: ['all'],
        }),
      shadow: () => this.new('Shadow', 'shadow', 'border').category('borders')
        .props({
          sizeOptions: SHADOW_SIZE,
          prefix: 'shadow',
          itens: ['width', 'color'],
          widthOptions: ['all'],
          generalTab: ShadowProperty,
          inputParser: (_, values) => shadowInputPerview(values),
        }),
    }
  }

  get svg() {
    return {
      fill: () => this.new('Fill', 'fill', 'color')
        .category('svg'),
      stroke: () => this.new('Stroke', 'stroke', 'border')
        .category('svg')
        .props({
          prefix: 'stroke',
          itens: ['width', 'color'],
          widthOptions: ['all'],
        }),
    }
  }

  get typography() {
    return {
      textColor: () => this.new('Color', 'textColor', 'textColor')
        .category('typography')
        .keys(['textColor', 'background.clip'])
        .hint(`Clip text makes the background show through the text. To see it, you must set a background (like a gradient). \n
        If you toggle it, change the background too.`),
      textAlign: () => this.new('Flex Align', 'textAlign', 'toggleGroupRow')
        .category('typography')
        .hint('Controls how the content is positioned inside a flex container.')
        .props({
          groups: [
            {
              props: { type: 'single', name: 'textAlign.horizontal', className: 'w-full' },
              options: [
                { value: 'left', icon: 'AlignLeftIcon', tooltip: 'Left', className: 'p-1' },
                { value: 'center', icon: 'AlignCenterIcon', tooltip: 'Center', className: 'p-1' },
                { value: 'right', icon: 'AlignRightIcon', tooltip: 'Right', className: 'p-1' },
                { value: 'justify', icon: 'AlignJustify', tooltip: 'Justify', className: 'p-1' },
              ],
            },
            {
              props: { type: 'single', name: 'textAlign.vertical', className: 'w-full' },
              options: [
                { value: 'top', icon: 'ArrowUpToLine', tooltip: 'Top', className: 'p-1' },
                { value: 'middle', icon: 'FlipVertical2', tooltip: 'Middle', className: 'p-1' },
                { value: 'bottom', icon: 'ArrowDownToLine', tooltip: 'Bottom', className: 'p-1' },
              ],
            },
          ],
        }),
      align: () => this.new('Align', 'align', 'toggleGroupRow')
        .category('typography')
        .hint('Controls the visual alignment of the element and its inner content.')
        .props({
          groups: [
            {
              props: { type: 'single', name: 'align.horizontal', className: 'w-full' },
              options: [
                { value: 'left', icon: 'AlignLeftIcon', tooltip: 'Left', className: 'p-1' },
                { value: 'center', icon: 'AlignCenterIcon', tooltip: 'Center', className: 'p-1' },
                { value: 'right', icon: 'AlignRightIcon', tooltip: 'Right', className: 'p-1' },
                { value: 'justify', icon: 'AlignJustify', tooltip: 'Justify', className: 'p-1' },
              ],
            },
            {
              props: { type: 'single', name: 'align.vertical', className: 'w-full' },
              options: [
                { value: 'top', icon: 'ArrowUpToLine', tooltip: 'Top', className: 'p-1' },
                { value: 'middle', icon: 'FlipVertical2', tooltip: 'Middle', className: 'p-1' },
                { value: 'bottom', icon: 'ArrowDownToLine', tooltip: 'Bottom', className: 'p-1' },
              ],
            },
          ],
        }),
      textAlignContainer: () => this.new('Text Align', 'textAlignContainer', 'textAlignContainer')
        .category('typography')
        .hint('Controls how text is positioned within the element.'),
      fontSize: () => this.new('Font Size', 'fontSize', 'predefinedArbitraryUnit')
        .category('typography')
        .props({
          name: 'fontSize',
          predefined: true,
          predefinedOptions: FONT_SIZE_SIZE,
          unitOptions: UNIT_OPTIONS,
          defaultUnit: 'px',
        }),
      fontFamily: () => this.new('Font Family', 'fontFamily.fontSerif', 'fontFamily')
        .category('typography')
        .keys(['fontFamily', 'fontSerif']),
      fontWeight: () => this.new('Font Weight', 'fontWeight', 'predefinedArbitraryUnit')
        .category('typography')
        .props({
          name: 'fontWeight',
          predefined: true,
          predefinedOptions: FONT_WEIGHTS,
          unitOptions: [{ label: 'Unitless', value: 'unitless' }],
          defaultUnit: 'unitless',
        }),
      letterSpacing: () => this.new('Letter Spacing', 'letterSpacing', 'predefinedArbitraryUnit')
        .category('typography')
        .props({
          name: 'letterSpacing',
          predefined: true,
          predefinedOptions: LETTER_SPACING_SIZE,
          unitOptions: UNIT_OPTIONS,
          defaultUnit: 'px',
        }),
      lineHeight: () => this.new('Line Height', 'lineHeight', 'arbitraryUnit')
        .category('typography')
        .props({
          name: 'lineHeight',
          unitOptions: [{ label: 'Unitless', value: 'unitless' }, ...UNIT_OPTIONS],
          defaultUnit: 'unitless',
        }),
      fontDecoration: () => this.new('Decoration', 'decoration.transform', 'textDecoration')
        .category('typography')
        .keys(['transform', 'decoration']),
      textShadow: () => this.new('Shadow', 'textShadow', 'border')
        .category('typography')
        .props({
          sizeOptions: TEXT_SHADOW_SIZE,
          title: 'Text Shadow',
          prefix: 'textShadow',
          itens: ['width', 'color'],
          widthOptions: ['all'],
          generalTab: ShadowProperty,
          inputParser: (_, { value = '', x = '', y = '', blur = '', unit = 'px' }) => value !== 'arbitrary' ? value : `${x ? `${x}${unit} ` : ''}${y ? `${y}${unit} ` : ''}${blur ? `blur:${blur}${unit}` : ''}`,
        }),
      paragraphSpacing: () => this.new('Paragraph Gap', 'paragraphSpacing', 'arbitraryUnit')
        .category('typography')
        .props({
          name: 'paragraphSpacing',
          unitOptions: [{ label: 'Unitless', value: 'unitless' }, ...UNIT_OPTIONS],
          defaultUnit: 'unitless',
        }),
      textIndent: () => this.new('Indent', 'textIndent', 'arbitraryUnit')
        .category('typography')
        .hint('Sets how much the first line of text is indented from the start of its container.')
        .props({
          name: 'textIndent',
          unitOptions: [{ label: 'Unitless', value: 'unitless' }, ...UNIT_OPTIONS],
          defaultUnit: 'unitless',
        }),
      lineClamp: () => this.new('Line Clamp', 'lineClamp', 'arbitraryUnit')
        .category('typography')
        .hint('Controls how many lines of text are visible before truncation with an ellipsis.')
        .props({
          name: 'lineClamp',
          unitOptions: [{ label: 'Unitless', value: 'unitless' }],
          defaultUnit: 'unitless',
        }),
      overflowWrap: () => this.group('Overflow Wrap', 'overflowWrap').unique()
        .category('typography')
        .props({ clearable: true })
        .hint('Controls if a long word should break.')
        .options(prop.options()
          .add('Break Word', 'wrap-break-word')
          .add('Anywhere', 'wrap-anywhere')
          .add('Normal', 'wrap-normal'),
        ),
      wordBreak: () => this.group('Word Break', 'wordBreak').unique()
        .category('typography')
        .props({ clearable: true })
        .hint('Controls how a long word should break.')
        .options(prop.options()
          .add('Normal', 'break-normal')
          .add('All', 'break-all')
          .add('Keep', 'break-keep'),
        ),
      textWrap: () => this.group('Wrap', 'textWrap').unique()
        .category('typography')
        .props({ clearable: true })
        .hint('Controls how the text wraps.')
        .options(prop.options()
          .add('Wrap', 'text-wrap')
          .add('No Wrap', 'text-nowrap')
          .add('Balance', 'text-balance')
          .add('Pretty', 'text-pretty'),
        ),
      textOverflow: () => this.group('Text Overflow', 'textOverflow').unique()
        .category('typography')
        .props({ clearable: true })
        .hint('Controls what to display when the text does NOT fit.')
        .options(prop.options()
          .add('Normal', 'truncate')
          .add('Ellipsis', 'text-ellipsis')
          .add('Clip', 'text-clip'),
        ),
      whitespace: () => this.group('White-space', 'whitespace').unique()
        .category('typography')
        .props({ clearable: true })
        .options(prop.options()
          .add('Normal', 'whitespace-normal')
          .add('No Wrap', 'whitespace-nowrap')
          .add('Pre', 'whitespace-pre')
          .add('Pre Line', 'whitespace-pre-line')
          .add('Pre Wrap', 'whitespace-pre-wrap')
          .add('Break Spaces', 'whitespace-break-spaces'),
        ),
      fontVariantNumeric: () => this.group('Variant Numeric', 'variantNumeric').unique()
        .category('typography')
        .props({ clearable: true, multi: true })
        .hint('Enables typographic features that control how numbers are drawn and aligned — availability depends on the font, since not all fonts support every numeric variant.')
        .options(prop.options()
          .add('Normal', 'normal-nums')
          .add('Ordinal', 'ordinal')
          .add('Slashed Zero', 'slashed-zero')
          .add('Lining Numeric', 'lining-nums')
          .add('Oldstyle Numeric', 'oldstyle-nums')
          .add('Proportional Numeric', 'proportional-nums')
          .add('Tabular Numeric', 'tabular-nums')
          .add('Diagonal Fractions', 'diagonal-fractions')
          .add('Stacked Fractions', 'stacked-fractions'),
        ),
    }
  }

  get flexGrid() {
    return {
      layoutType: () => this.toggle('Type', 'layoutType')
        .category('flexGrid')
        .props({ type: 'single' })
        .options(this.options()
          .add('Flex', 'flex', { content: 'Flex' })
          .add('Grid', 'grid', { content: 'Grid' }),
        ),
      flexAlign: () => this.toggle('Align', 'flex.align')
        .category('flexGrid')
        .props({
          type: 'single',
          dropdownLabel: 'Flex Align',
        })
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('flex')
        })
        .options(this.options()
          .add('Start', 'items-start', { icon: 'AlignVerticalJustifyStart', tooltip: 'Start' })
          .add('Center', 'items-center', { icon: 'AlignVerticalSpaceAround', tooltip: 'Center' })
          .add('End', 'items-end', { icon: 'AlignVerticalJustifyEnd', tooltip: 'End' })
          .add('Stretch', 'items-stretch', { icon: 'StretchVertical', tooltip: 'Stretch' }),
        ),
      flexDirection: () => this.toggle('Direction', 'flex.direction')
        .category('flexGrid')
        .props({
          type: 'single',
          defaultValue: 'flex-row',
          dropdownLabel: 'Flex Direction',
        })
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('flex')
        })
        .options(this.options()
          .add('Row', 'flex-row', { icon: 'MoveHorizontal', tooltip: 'Horizontal' })
          .add('Column', 'flex-col', { icon: 'MoveVertical', tooltip: 'Vertical' }),
        ),
      flexDistribute: () => this.toggle('Distribute', 'flex.distribute')
        .category('flexGrid')
        .props({
          type: 'single',
          dropdownLabel: 'Flex Distribute',
        })
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('flex')
        })
        .options(this.options()
          .add('Start', 'justify-start', { icon: 'AlignHorizontalJustifyStart', tooltip: 'Start' })
          .add('Center', 'justify-center', { icon: 'AlignHorizontalJustifyCenter', tooltip: 'Center' })
          .add('End', 'justify-end', { icon: 'AlignHorizontalJustifyEnd', tooltip: 'End' })
          .add('Space Between', 'justify-between', { icon: 'AlignHorizontalSpaceBetween', tooltip: 'Space Between' })
          .add('Space Around', 'justify-around', { icon: 'AlignHorizontalSpaceAround', tooltip: 'Space Around' })
          .add('Space Evenly', 'justify-evenly', { icon: 'GalleryHorizontal', tooltip: 'Space Evenly' }),
        ),
      flexWrap: () => this.toggle('Wrap', 'flex.wrap')
        .category('flexGrid')
        .props({
          type: 'single',
          dropdownLabel: 'Flex Wrap',
          defaultValue: 'flex-nowrap',
        })
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('flex')
        })
        .options(this.options()
          .add('Yes', 'flex-wrap', { content: 'Yes' })
          .add('No', 'flex-nowrap', { content: 'No' }),
        )
        .hint('Controls whether flex items wrap onto multiple lines or stay on a single line.'),
      flexReverse: () => this.toggle('Reverse', 'flex.reverse')
        .category('flexGrid')
        .props({
          type: 'single',
          dropdownLabel: 'Flex Reverse',
        })
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('flex')
        })
        .options(this.options()
          .add('Yes', true, { content: 'Yes' })
          .add('No', false, { content: 'No' }),
        )
        .hint('Reverse the order of items in row/column.'),
      flexGap: () => this.new('Gap', 'flex', 'gap')
        .category('flexGrid')
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('flex')
        })
        .props({
          name: 'flex.gap',
          dropdownLabel: 'Flex Gap',
        }),
      flexStretch: () => this.new('Stretch', 'flex.grow.shrink', 'flexStretch')
        .category('flexGrid')
        .keys(['flex.grow', 'flex.shrink'])
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('flex')
        })
        .props({
          dropdownLabel: 'Flex Stretch',
        }),
      gridDirection: () => this.toggle('Direction', 'grid.direction')
        .category('flexGrid')
        .props({
          type: 'single',
          defaultValue: 'grid-flow-row',
          dropdownLabel: 'Grid Direction',
        })
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('grid')
        })
        .options(this.options()
          .add('Col', 'grid-flow-row', { icon: 'MoveHorizontal', tooltip: 'Horizontal' })
          .add('Row', 'grid-flow-col', { icon: 'MoveVertical', tooltip: 'Vertical' }),
        ),
      gridDense: () => this.toggle('Dense', 'grid.dense')
        .category('flexGrid')
        .props({
          type: 'single',
          dropdownLabel: 'Grid Dense',
          defaultValue: 'no',
        })
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('grid')
        })
        .options(this.options()
          .add('Yes', 'yes', { content: 'Yes' })
          .add('No', 'no', { content: 'No' }),
        )
        .hint('It tries to fill gaps by automatically reordering items.'),
      gridAlign: () => this.toggle('Align', 'grid.align')
        .category('flexGrid')
        .props({
          type: 'single',
          dropdownLabel: 'Grid Align',
        })
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('grid')
        })
        .options(this.options()
          .add('Start', 'justify-items-start', { icon: 'AlignHorizontalJustifyStart', tooltip: 'Start' })
          .add('Center', 'justify-items-center', { icon: 'AlignHorizontalJustifyCenter', tooltip: 'Center' })
          .add('End', 'justify-items-end', { icon: 'AlignHorizontalJustifyEnd', tooltip: 'End' })
          .add('Stretch', 'justify-items-stretch', { icon: 'StretchHorizontal', tooltip: 'Stretch' }),
        ),
      gridTemplate: () => this.new('Template', 'grid.rows.columns', 'gridTemplate')
        .category('flexGrid')
        .keys(['grid.rows', 'grid.columns'])
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('grid')
        })
        .props({
          dropdownLabel: 'Grid Template',
        }),
      gridGap: () => this.new('Gap', 'grid', 'gap')
        .category('flexGrid')
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('grid')
        })
        .props({
          name: 'grid.gap',
          dropdownLabel: 'Grid Gap',
        }),
      gridWidth: () => this.new('Items Width', 'grid.width', 'gridSize')
        .category('flexGrid')
        .keys(['grid.width', 'grid.widthFixed'])
        .props({
          sizeName: 'grid.width',
          sizeFixedName: 'grid.widthFixed',
          dropdownLabel: 'Grid Width',
          options: [
            { value: 'auto', label: 'Auto' },
            { value: 'fixed', label: 'Fixed' },
          ],
        })
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('grid')
        }),
      gridHeight: () => this.new('Items Height', 'grid.height', 'gridSize')
        .category('flexGrid')
        .keys(['grid.height', 'grid.heightFixed'])
        .props({
          sizeName: 'grid.height',
          sizeFixedName: 'grid.heightFixed',
          dropdownLabel: 'Grid Height',
          options: [
            { value: 'fillcontainer', label: 'Fill Container' },
            { value: 'fitcontent', label: 'Fit Content' },
            { value: 'fixed', label: 'Fixed' },
          ],
        })
        .useShouldRender(() => {
          const { value, rawValue } = useCraftProp('layoutType')
          return [value, rawValue].includes('grid')
        }),
      gridItemColumn: () => this.new('Colspan', 'grid.itemColumn', 'gridCount')
        .category('flexGrid')
        .props({
          name: 'grid.itemColumn',
          fullOption: true,
          dropdownLabel: 'Grid Item Column',
        }),
      gridItemRow: () => this.new('Rowspan', 'grid.itemRow', 'gridCount')
        .category('flexGrid')
        .props({
          name: 'grid.itemRow',
          fullOption: true,
          dropdownLabel: 'Grid Item Row',
        }),
    }
  }

  get layout() {
    return {
      aspectRatio: () => this.new('Aspect Ratio', 'aspectRatio', 'predefinedArbitraryUnit')
        .category('layout')
        .hint('Locks the element to a fixed width-to-height ratio, ensuring it always keeps the same shape.')
        .props({
          name: 'aspectRatio',
          predefined: true,
          predefinedOptions: ASPECT_RATIO,
          unitOptions: [{ label: 'Unitless', value: 'unitless' }],
          defaultUnit: 'unitless',
        }),
      overflow: () => this.new('Overflow', 'overflow', 'overflow')
        .category('layout'),
      position: () => this.new('Position', 'position', 'position')
        .category('layout'),
      float: () => this.group('Float', 'float').unique()
        .category('layout')
        .props({ clearable: true })
        .options(prop.options()
          .add('Right', 'float-right')
          .add('Left', 'float-left')
          .add('Start', 'float-start')
          .add('End', 'float-end')
          .add('None', 'float-none'),
        ),
      boxSizing: () => this.toggle('Box Sizing', 'boxSizing')
        .category('layout')
        .props({ type: 'single' })
        .hint('Controls how width and height are calculated — whether padding and borders are included in the element’s total size.')
        .options(this.options()
          .add('Border', 'box-border', { content: 'Border' })
          .add('Content', 'box-content', { content: 'Content' }),
        ),
      overscroll: () => this.new('Overscroll', 'overscroll', 'overscroll')
        .category('layout')
        .hint('Controls how scrolling behaves at boundaries.'),
      clear: () => this.group('Clear', 'clear').unique()
        .category('layout')
        .hint('Prevents an element from sitting beside floated elements on a given side.')
        .props({ clearable: true })
        .options(prop.options()
          .add('Left', 'clear-left')
          .add('Right', 'clear-right')
          .add('Both', 'clear-both')
          .add('Start', 'clear-start')
          .add('End', 'clear-end')
          .add('None', 'clear-none'),
        ),
      boxDecorationBreak: () => this.toggle('Box Break', 'boxDecorationBreak')
        .category('layout')
        .props({ type: 'single' })
        .hint('Controls whether backgrounds, borders, and padding are applied as a single box or repeated for each fragmented line.')
        .options(this.options()
          .add('Clone', 'box-decoration-clone', { content: 'Clone' })
          .add('Slice', 'box-decoration-slice', { content: 'Slice' }),
        ),
      display: () => this.group('Display', 'display').unique()
        .category('layout')
        .props({ clearable: true })
        .options(prop.options()
          .add('Inline', 'inline')
          .add('Block', 'block')
          .add('Inline Block', 'inline-block')
          .add('Flow Root', 'flow-root')
          .add('Contents', 'contents')
          .add('Table', 'table')
          .add('Inline Table', 'inline-table')
          .add('Table Caption', 'table-caption')
          .add('Table Cell', 'table-cell')
          .add('Table Column', 'table-column')
          .add('Table Column Group', 'table-column-group')
          .add('Table Footer Group', 'table-footer-group')
          .add('Table Header Group', 'table-header-group')
          .add('Table Row Group', 'table-row-group')
          .add('Table Row', 'table-row')
          .add('List Item', 'list-item')
          .add('SR Only', 'sr-only'),
        ),
      zindex: () => this.new('Z-index', 'zindex', 'zindex')
        .category('layout'),
    }
  }

  get interactivity() {
    return {
      accentColor: () => this.new('Accent', 'accentColor', 'color')
        .category('interactivity')
        .hint(`Overrides the browser’s default color.`),
      userSelect: () => this.group('User Select', 'userSelect').unique().category('appearance')
        .props({ clearable: true })
        .hint('Controls whether the user can select text in an element.')
        .options(prop.options()
          .add('None', 'select-none')
          .add('Text', 'select-text')
          .add('All', 'select-all')
          .add('Auto', 'select-auto'),
        ),
      willChange: () => this.select('Will Change', 'willChange')
        .category('interactivity')
        .hint('Hints the browser about which properties will change soon, helping optimize animations.')
        .props({
          clearable: true,
          creatable: true,
          multi: true,
          options: [
            { value: 'transform', label: 'Transform' },
            { value: 'scroll', label: 'Scroll' },
            { value: 'contents', label: 'Contents' },
          ],
        }),
      resize: () => this.group('Resize', 'resize').unique()
        .category('interactivity')
        .props({ clearable: true })
        .hint('Controls whether the user can resize an element.')
        .options(prop.options()
          .add('All', 'resize')
          .add('Horizontal', 'resize-x')
          .add('Vertical', 'resize-y')
          .add('None', 'resize-none'),
        ),
      fieldSizing: () => this.toggle('Field Sizing', 'fieldSizing')
        .category('interactivity')
        .props({ type: 'single' })
        .options(this.options()
          .add('Fixed', 'field-sizing-fixed', { content: 'Fixed' })
          .add('Content', 'field-sizing-content', { content: 'Content' }),
        ),
      cursor: () => this.group('Cursor', 'cursor').unique()
        .category('interactivity')
        .props({
          clearable: true,
        })
        .options(prop.options()
          .add('Auto', 'cursor-auto')
          .add('Default', 'cursor-default')
          .add('Pointer', 'cursor-pointer')
          .add('Wait', 'cursor-wait')
          .add('Text', 'cursor-text')
          .add('Move', 'cursor-move')
          .add('Help', 'cursor-help')
          .add('Not Allowed', 'cursor-not-allowed')
          .add('None', 'cursor-none')
          .add('Context Menu', 'cursor-context-menu')
          .add('Progress', 'cursor-progress')
          .add('Cell', 'cursor-cell')
          .add('Crosshair', 'cursor-crosshair')
          .add('Vertical Text', 'cursor-vertical-text')
          .add('Alias', 'cursor-alias')
          .add('Copy', 'cursor-copy')
          .add('No Drop', 'cursor-no-drop')
          .add('Grab', 'cursor-grab')
          .add('Grabbing', 'cursor-grabbing')
          .add('All Scroll', 'cursor-all-scroll')
          .add('Column Resize', 'cursor-col-resize')
          .add('Row Resize', 'cursor-row-resize')
          .add('North Resize', 'cursor-n-resize')
          .add('East Resize', 'cursor-e-resize')
          .add('South Resize', 'cursor-s-resize')
          .add('West Resize', 'cursor-w-resize')
          .add('North East Resize', 'cursor-ne-resize')
          .add('North West Resize', 'cursor-nw-resize')
          .add('South East Resize', 'cursor-se-resize')
          .add('South West Resize', 'cursor-sw-resize')
          .add('East West Resize', 'cursor-ew-resize')
          .add('North South Resize', 'cursor-ns-resize')
          .add('North East South West Resize', 'cursor-nesw-resize')
          .add('North West South East Resize', 'cursor-nwse-resize')
          .add('Zoom In', 'cursor-zoom-in')
          .add('Zoom Out', 'cursor-zoom-out'),
        ),
      nativeStyle: () => this.new('Native Style', 'nativeStyle', 'nativeStyle')
        .category('interactivity'),
      groupPropagation: () => this.switch('Group', 'groupPropagation')
        .category('interactivity')
        .ignoreDevice()
        .hint('Propagates the state to the parent element.'),
      peerPropagation: () => this.switch('Peer', 'peerPropagation')
        .category('interactivity')
        .hint('Propagates the state to the peer elements.')
        .ignoreDevice(),
      pointerEvents: () => this.toggle('Pointer Events', 'pointerEvents')
        .category('interactivity')
        .props({ type: 'single' })
        .options(this.options()
          .add('Auto', 'pointer-events-auto', { content: 'Auto' })
          .add('None', 'pointer-events-none', { content: 'None' }),
        ),
      scroll: () => this.new('Scroll', 'scroll', 'scroll')
        .category('interactivity'),
    }
  }

  get appearance() {
    return {
      background: () => this.new('Background', 'background', 'background')
        .category('appearance')
        .fixedLabel(true),
      frame: () => this.new('Spacing', 'space.size', 'frame')
        .category('appearance')
        .keys(['space.margin', 'space.padding', 'size.width', 'size.height', 'size.min.width', 'size.min.height', 'size.max.width', 'size.max.height'])
        .fixedLabel(true),
      visibility: () => this.new('Visibility', 'opacity.hide', 'visibility')
        .category('appearance')
        .keys(['opacity', 'hide']),
    }
  }

  get effect() {
    return {
      filters: () => this.new('Filters', 'filters', 'filters')
        .category('effect')
        .fixedLabel(true),
      transform: () => this.new('Transform', 'transforms', 'transform')
        .category('effect'),
      transition: () => this.new('Transition', 'transition', 'transition')
        .category('effect'),
      animation: () => this.new('Animation', 'animation', 'animation')
        .category('effect')
        .fixedLabel(true),
      mask: () => this.new('Mask', 'mask', 'mask')
        .category('effect'),
    }
  }

  get default() {
    return {
      attributes: () => this.new('Attributes', 'attributes', 'attributes')
        .category('default')
        .blockClear()
        .ignoreDevice()
        .own(),
      contentUtility: () => this.text('Content Utility', 'contentUtility')
        .category('default')
        .useShouldRender(() => {
          const { selectedUtilities } = useUtilities('layoutType')
          return selectedUtilities.includes('before') || selectedUtilities.includes('after')
        }),
    }
  }

  get all() {
    return {
      ...this.default,
      ...this.appearance,
      ...this.typography,
      ...this.border,
      ...this.flexGrid,
      ...this.effect,
      ...this.layout,
      ...this.interactivity,
      ...this.svg,
    }
  }

  get predefined() {
    return {
      ...this.all,
      imageAlt: () => this.text('Alt text', 'attributes[alt]')
        .category('standard')
        .ignoreDevice(true)
        .useShouldRender(() => {
          const { data: { custom, name } } = useNode(({ data }) => ({ data }))

          return custom.showImageProps || ['image'].includes(name)
        }),
      imageAttributes: () => this.new('Image', 'attributes', 'image')
        .component(prop.types.image)
        .category('standard')
        .own()
        .ignoreDevice(true)
        .useShouldRender(() => {
          const { data: { name } } = useNode(({ data }) => ({ data }))

          return ['image'].includes(name)
        }),
      imageAlign: () => this.new('Align', 'imageAlign', 'imageAlign')
        .category('appearance')
        .useShouldRender(() => {
          const { data: { custom, name } } = useNode(({ data }) => ({ data }))

          return custom.showImageProps || ['image'].includes(name)
        }),
      imageFit: () => this.select('Fit', 'image.fit')
        .props({
          clearable: true,
          variant: 'accent',
        })
        .options([
          { value: 'object-contain', label: 'Contain' },
          { value: 'object-cover', label: 'Cover' },
          { value: 'object-fill', label: 'Fill' },
          { value: 'object-none', label: 'None' },
          { value: 'object-scale-down', label: 'Scale Down' },
        ])
        .category('object')
        .useShouldRender(() => {
          const { data: { custom, name } } = useNode(({ data }) => ({ data }))

          return custom.showImageProps || ['image'].includes(name)
        }),
      imagePosition: () => this.new('Position', 'image.position', 'positionImage')
        .props({
          prefix: 'object-',
        })
        .category('object')
        .useShouldRender(() => {
          const { data: { custom, name } } = useNode(({ data }) => ({ data }))

          return custom.showImageProps || ['image'].includes(name)
        }),
      variant: (label, name) => this.new(label, name).component(prop.types.variant_select).category('default'),
      boolean: (label, name) => this.group(label, name).unique()
        .options(prop.options()
          .add('on', true)
          .add('off', false)),
      customClass: () => this.select.classes('Class', 'customClassName')
        .category('default')
        .props({
          clearable: true,
          multi: true,
        }),
      content: () => this.text('Content', 'content').category('default').own(),
      textContent: () => this.predefined.content().component(this.types.textContent).own(),
      url: () => this.text('URL', 'url').category('default').own(),
      target: () => this.group('Target', 'target').unique().own().category('default')
        .options(prop.options()
          .add('Self', '_self')
          .add('Blank', '_blank')
          .add('Parent', '_parent')
          .add('Top', '_top'),
        ),
      typography: () => [
        this.predefined.textColor(),
        this.predefined.textAlign(),
        this.predefined.letterSpacing(),
        this.predefined.lineHeight(),
        this.predefined.fontSize(),
        this.predefined.fontWeight(),
        this.predefined.fontFamily(),
        this.predefined.fontDecoration(),
        this.predefined.textShadow(),
        this.predefined.textWrap(),
      ],
      borders: () => [
        this.predefined.border(),
        this.predefined.ring(),
        this.predefined.outline(),
        this.predefined.shadow(),
      ],
      appearance: () => [
        this.predefined.background(),
        this.predefined.visibility(),
        this.predefined.overflow(),
        this.predefined.frame(),
        this.predefined.position(),
        this.predefined.float(),
        this.predefined.transition(),
        this.predefined.animation(),
        this.predefined.transform(),
        this.predefined.cursor(),
        this.predefined.display(),
        // this.predefined.peerPropagation(),
        this.predefined.nativeStyle(),
        this.predefined.contentUtility(),
        this.predefined.filters(),
        this.predefined.zindex(),
        this.predefined.pointerEvents(),
      ],
      layout: () => [
        this.predefined.layoutType(),
        this.predefined.flexDirection(),
        this.predefined.flexReverse(),
        this.predefined.flexDistribute(),
        this.predefined.flexAlign(),
        this.predefined.flexWrap(),
        this.predefined.flexGap(),
        this.predefined.flexStretch(),
        this.predefined.gridDirection(),
        this.predefined.gridDense(),
        this.predefined.gridAlign(),
        this.predefined.gridTemplate(),
        this.predefined.gridGap(),
        this.predefined.gridWidth(),
        this.predefined.gridHeight(),
      ],
      flex: () => [
        this.predefined.flexDirection(),
        this.predefined.flexReverse(),
        this.predefined.flexDistribute(),
        this.predefined.flexAlign(),
        this.predefined.flexWrap(),
        this.predefined.flexGap(),
        this.predefined.flexStretch(),
      ],
      grid: () => [
        this.predefined.gridDirection(),
        this.predefined.gridDense(),
        this.predefined.gridAlign(),
        this.predefined.gridTemplate(),
        this.predefined.gridGap(),
        this.predefined.gridWidth(),
        this.predefined.gridHeight(),
      ],
      gridItem: () => [
        this.predefined.gridItemColumn(),
        this.predefined.gridItemRow(),
      ],
      container: () => [
        this.predefined.textAlignContainer(),
        // this.predefined.groupPropagation(),
      ],
      image: () => [
        this.predefined.imageAlt(),
        this.predefined.imageAttributes(),
        this.predefined.imageAlign(),
        this.predefined.imageFit(),
        this.predefined.imagePosition(),
      ],
      children: () => this.switch('Allow Children', 'allowChildren')
        .category('default')
        .ignoreDevice(true),
    }
  }
}

export function variant(preview, label = 'Variant', title) {
  const optionsBuilder = prop.options()
  return {
    prop: label ? prop.predefined.variant(label, 'variant').options(optionsBuilder) : null,
    variants: [],
    title,

    add(key, label, props, opts) {
      optionsBuilder.add(label, key, { props })
      props.variant = key
      this.variants.push({ key, label, preview, props, ...opts })
      return this
    },
  }
}
